import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { emptySubContact } from "../utils/subContacts";

/**
 * Edits a marketing contact's sub-contacts (other people at the group) inside
 * the Edit Contact dialog. Changes are local until the dialog is saved,
 * except Send, which the parent saves as part of recording the send.
 */
export default function SubContactsEditor({
  subContacts,
  onChange,
  errors,
  onClearError,
  phoneTypes,
  formatPhoneNumber,
  formatDate,
  onSend,
  sendDisabledReason,
  onMakePrimary,
}) {
  const update = (id, patch) =>
    onChange(subContacts.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const field = (sub, key) => (e) => {
    update(sub.id, { [key]: e.target.value });
    onClearError(`sub_${sub.id}_${key}`);
  };

  const remove = (id) => onChange(subContacts.filter((s) => s.id !== id));

  return (
    <>
      {subContacts.map((sub, index) => {
        const nameError = errors[`sub_${sub.id}_name`];
        const emailError = errors[`sub_${sub.id}_email`];
        const sendBlocked =
          sendDisabledReason ||
          (sub.unsubscribed && "This person has unsubscribed") ||
          (!sub.email.trim() && "Add an email address to send");
        return (
          <Col md={12} key={sub.id}>
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{sub.name.trim() || `Contact ${index + 2}`}</strong>
                <div className="text-nowrap">
                  {sub.unsubscribed && (
                    <span className="badge bg-danger text-white mr-2">
                      Unsubscribed
                    </span>
                  )}
                  {formatDate(sub.date_sent) && (
                    <span className="text-muted">
                      Last Sent {formatDate(sub.date_sent)}
                    </span>
                  )}
                </div>
              </div>
              <Row>
                <Col md={6} lg={12} xl={6}>
                  <Form.Group controlId={`sub-${sub.id}-name`}>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Name"
                      className="inputclass form-control"
                      value={sub.name}
                      onChange={field(sub, "name")}
                      isInvalid={!!nameError}
                    />
                    {nameError && <span style={{ color: "red" }}>{nameError}</span>}
                  </Form.Group>
                </Col>
                <Col md={6} lg={12} xl={6}>
                  <Form.Group controlId={`sub-${sub.id}-title`}>
                    <Form.Label>Role / Title</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Associate Pastor"
                      className="inputclass form-control"
                      value={sub.title}
                      onChange={field(sub, "title")}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId={`sub-${sub.id}-email`}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      className="inputclass form-control"
                      value={sub.email}
                      onChange={field(sub, "email")}
                      isInvalid={!!emailError}
                    />
                    {emailError && <span style={{ color: "red" }}>{emailError}</span>}
                  </Form.Group>
                </Col>
                {[1, 2].map((n) => (
                  <Col md={12} key={n}>
                    <Form.Group>
                      <Form.Label htmlFor={`sub-${sub.id}-phone${n}`}>
                        Phone {n}
                      </Form.Label>
                      <div className="d-flex">
                        <Form.Control
                          as="select"
                          aria-label={`Phone ${n} type`}
                          className="inputclass form-control mr-2"
                          style={{ flex: "0 0 12rem" }}
                          value={sub[`phone${n}_type`] || ""}
                          onChange={field(sub, `phone${n}_type`)}
                        >
                          <option value="">Select Type</option>
                          {phoneTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Control
                          type="tel"
                          id={`sub-${sub.id}-phone${n}`}
                          placeholder="Phone Number"
                          className="inputclass form-control"
                          value={sub[`phone${n}`] || ""}
                          onChange={(e) =>
                            update(sub.id, {
                              [`phone${n}`]: formatPhoneNumber(e.target.value),
                            })
                          }
                        />
                      </div>
                    </Form.Group>
                  </Col>
                ))}
              </Row>
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <Form.Check
                  type="checkbox"
                  id={`sub-${sub.id}-unsubscribed`}
                  label="Unsubscribed"
                  checked={!!sub.unsubscribed}
                  onChange={(e) =>
                    update(sub.id, {
                      unsubscribed: e.target.checked,
                      ...(e.target.checked
                        ? {}
                        : { unsubscribed_reason: "", unsubscribed_date: "" }),
                    })
                  }
                />
                <div className="mt-2 mt-md-0">
                  <span title={sendBlocked || "Send the marketing email to this person"}>
                    <Button
                      variant="success"
                      size="sm"
                      className="mr-2"
                      onClick={() => onSend(sub)}
                      disabled={!!sendBlocked}
                      style={sendBlocked ? { pointerEvents: "none" } : undefined}
                    >
                      <span className="icon-mail mr-1"></span>Send
                    </Button>
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mr-2"
                    onClick={() => onMakePrimary(sub)}
                  >
                    Make Primary
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => remove(sub.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        );
      })}
      <Col md={12}>
        <Button
          variant="link"
          size="sm"
          className="p-0 mb-3"
          style={{ textDecoration: "none" }}
          onClick={() => onChange([...subContacts, emptySubContact()])}
        >
          + Add Contact
        </Button>
      </Col>
    </>
  );
}
